import AccessListItem from "@components/Accesses/components/AccessListItem";
import MiniAccessCard from "@components/Accesses/components/MiniAccessCard";
import { EmptyAccessScreen } from "@components/Accesses/EmptyAccessScreen";
import LArtwork from "@components/LArtwork";
import LBox from "@components/LBox";
import LFlatList from "@components/LFlatList";
import LGradient from "@components/LGradient";
import LoadingScreen from "@components/LoadingScreen";
import LPressable from "@components/LPressable";
import LScreen from "@components/LScreen";
import LScrollView from "@components/LScrollView";
import LText from "@components/LText";
import {
  useAllAccessesPaginated,
  useAccessesPinned,
} from "@hooks/trpc/useAccesses";
import {
  getAccessImage,
  prefetchAccessDetailHeroImage,
} from "@utils/accesses/access";
import { AccessTypeOptions } from "@libs/utils";
import { fonts, theme } from "@theme";
import { router } from "expo-router";
import { useMemo, useState } from "react";
import { Platform } from "react-native";
import LIcon from "@components/LIcon";
import AccessTypeChip from "@components/Accesses/components/AccessTypeChip";

type CategoryTextProps = {
  text: string;
  active: boolean;
  onPress: () => void;
};

const CategoryText = ({ text, active, onPress }: CategoryTextProps) => {
  return (
    <LPressable sentryEventName="experiences_category" onPress={onPress}>
      <LBox alignItems="center">
        <LText
          variant="button"
          color={active ? "white" : "lightGrey"}
          fontFamily={active ? undefined : fonts.regular}
        >
          {text}
        </LText>
        <LBox
          backgroundColor={["activeIndicatorLeft", "activeIndicatorRight"]}
          width="100%"
          height={2}
          opacity={active ? 1 : 0}
          borderRadius="max"
          marginTop="s1"
        />
      </LBox>
    </LPressable>
  );
};

export const AccessesScreen = () => {
  const { data: accessesPinned } = useAccessesPinned();
  const { data: accesses, isLoading } = useAllAccessesPaginated();
  const featuredAccess = accessesPinned?.at(0) ?? accesses?.at(0);

  // Dynamic categories based on available experiences
  const categories = useMemo(() => {
    const baseCategories = [
      {
        key: "all" as const,
        text: "Trending",
      },
      {
        key: "event" as const,
        text: "Event",
      },
      {
        key: "video" as const,
        text: "Video",
      },
      {
        key: "merch" as const,
        text: "Merch",
      },
      {
        key: "music" as const,
        text: "Music",
      },
      {
        key: "post" as const,
        text: "Post",
      },
      {
        key: "experience" as const,
        text: "Experience",
      },
      {
        key: "chat" as const,
        text: "Chat",
      },
    ];

    if (!accesses?.length) return [];

    return baseCategories.filter((category) => {
      // Only show "all" if there are 2+ experiences
      if (category.key === "all") {
        return accesses.length > 1;
      }
      // Show other categories if they have at least one experience
      return accesses.some((access) => access.type === category.key);
    });
  }, [accesses]);

  // Determine initial active category based on experiences
  const defaultCategory = useMemo(() => {
    if (!accesses?.length) return "all";
    if (accesses.length === 1) {
      const type = accesses.at(0)?.type as AccessTypeOptions;
      return type ?? "all";
    }
    return "all";
  }, [accesses]);

  const [activeCategory, setActiveCategory] =
    useState<(typeof categories)[number]["key"]>(defaultCategory);

  const remainingExperiences = useMemo(() => {
    // Remove the featured experience
    const remaining =
      accesses?.filter((access) => access.id !== featuredAccess?.id) ?? [];
    return remaining;
  }, [accesses, featuredAccess]);

  const CAROUSEL_LIMIT = 5;

  // Pre-compute all carousel slots (5 trending + 5 event + 5 music)
  // so upcoming always excludes the same set regardless of active tab
  const { carouselByCategory, allCarouselIds } = useMemo(() => {
    if (!remainingExperiences)
      return {
        carouselByCategory: { all: [], event: [], music: [] },
        allCarouselIds: new Set<string>(),
      };
    const all = remainingExperiences.slice(0, CAROUSEL_LIMIT);
    const event = remainingExperiences
      .filter((a) => a.type === "event")
      .slice(0, CAROUSEL_LIMIT);
    const music = remainingExperiences
      .filter((a) => a.type === "music")
      .slice(0, CAROUSEL_LIMIT);
    const ids = new Set([...all, ...event, ...music].map((a) => a.id));
    return { carouselByCategory: { all, event, music }, allCarouselIds: ids };
  }, [remainingExperiences]);

  const carouselExperiences = useMemo(() => {
    switch (activeCategory) {
      case "all":
        return carouselByCategory.all;
      case "event":
        return carouselByCategory.event;
      case "music":
        return carouselByCategory.music;
      default:
        return [];
    }
  }, [activeCategory, carouselByCategory]);

  const upcomingExperiences = useMemo(() => {
    return remainingExperiences.filter((a) => !allCarouselIds.has(a.id));
  }, [remainingExperiences, allCarouselIds]);

  const featuredExperienceSubtitle = useMemo(() => {
    if (featuredAccess?.type === "event") {
      if (
        featuredAccess.venue &&
        featuredAccess.city &&
        featuredAccess.country
      ) {
        return `${featuredAccess.venue} (${featuredAccess.city}, ${featuredAccess.country})`;
      }
      if (featuredAccess.venue && featuredAccess.city) {
        return `${featuredAccess.venue}, ${featuredAccess.city}`;
      }
      if (featuredAccess.city && featuredAccess.country) {
        return `${featuredAccess.city} • ${featuredAccess.country}`;
      }
      return featuredAccess.city || featuredAccess.venue || "";
    }
    if (featuredAccess?.type === "music") {
      return featuredAccess.songs.at(0)?.title;
    }
    return "";
  }, [featuredAccess]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isLoading && (!accesses || accesses.length === 0)) {
    return <EmptyAccessScreen />;
  }

  return (
    <LScreen enableAutomaticTopPadding={true}>
      <LScrollView
        style={{ paddingTop: 20 }}
        contentContainerStyle={{ paddingBottom: 20 }}
      >
        <LBox style={{ gap: 22 }}>
          {featuredAccess ? (
            <LPressable
              onPress={() => {
                prefetchAccessDetailHeroImage(featuredAccess);
                router.push(`/access/${featuredAccess.id}`);
              }}
              sentryEventName="AccessesScreen.FeaturedAccess"
            >
              <LBox
                borderRadius="m-l"
                overflow="hidden"
                style={{
                  borderWidth: 1,
                  borderColor: "rgba(255, 255, 255, 0.2)",
                  alignSelf: "center",
                  width: Platform.OS === "web" ? "100%" : undefined,
                }}
              >
                <LArtwork
                  url={getAccessImage(featuredAccess)}
                  imageSize={
                    featuredAccess.type === "event" &&
                    featuredAccess.coverImageUrl
                      ? "large"
                      : undefined
                  }
                  width="100%"
                  aspectRatio={2.5}
                  imageStyle={{ flex: 1, aspectRatio: undefined }}
                />
                <AccessTypeChip access={featuredAccess} placement="top-right" />
                {/* Gradient overlay for better text readability */}
                <LGradient
                  gradientDirection="vertical"
                  colors={["transparent", "rgba(0,0,0,0.4)", "rgba(0,0,0,0.8)"]}
                  locations={[0, 0.5, 1]}
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "100%",
                  }}
                />

                <LBox
                  position="absolute"
                  p="s3"
                  gap="s1"
                  bottom={0}
                  left={0}
                  right={0}
                >
                  <LText
                    variant="largeHeader"
                    style={{
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.3,
                      shadowRadius: 2,
                      fontWeight: "700",
                      fontSize: 20,
                    }}
                  >
                    {`${featuredAccess.artistName}`}
                  </LText>
                  <LText
                    variant="bodyL"
                    color="lightGrey"
                    style={{
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.3,
                      shadowRadius: 2,
                      fontWeight: "500",
                      fontSize: 14,
                    }}
                  >
                    {featuredExperienceSubtitle}
                  </LText>
                </LBox>
              </LBox>
            </LPressable>
          ) : null}
          <LBox gap="s3">
            <LBox flexDirection="row" gap="s4">
              {categories.map((category) => (
                <CategoryText
                  key={category.key}
                  text={category.text}
                  active={activeCategory === category.key}
                  onPress={() => setActiveCategory(category.key)}
                />
              ))}
            </LBox>
            <LFlatList
              horizontal
              showsHorizontalScrollIndicator={false}
              data={carouselExperiences}
              renderItem={({ item }) => <MiniAccessCard access={item} />}
              contentContainerStyle={{ columnGap: theme.spacing.s4 }}
              disableAutomaticPadding={true}
            />
          </LBox>
          <LBox gap="s3" style={{ top: -4 }}>
            <LBox
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap="s2"
            >
              <LText variant="listItem">Upcoming</LText>
              <LPressable
                sentryEventName="experiences_see_all"
                onPress={() => {
                  router.navigate(`/(app)/(tabs)/explore/list-all-accesses`);
                }}
              >
                <LBox
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="center"
                  gap="s0_5"
                >
                  <LText
                    color="searchResultSeeAllText"
                    fontWeight="600"
                    fontSize={16}
                    letterSpacing={0.2}
                  >
                    See all
                  </LText>
                  <LIcon
                    name="chevron"
                    opacity={0.5}
                    rotate={270}
                    size={12.5}
                    stroke="white"
                  />
                </LBox>
              </LPressable>
            </LBox>
            <LBox gap="s4">
              {upcomingExperiences
                ?.slice(0, 5)
                .map((access) => (
                  <AccessListItem key={access.id} access={access} />
                ))}
            </LBox>
          </LBox>
        </LBox>
      </LScrollView>
    </LScreen>
  );
};
